import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  X, User, Briefcase, GraduationCap, Shield, Eye, EyeOff, RefreshCw, Copy, Check, AlertCircle,
} from 'lucide-react';
import { getUserDepartmentIds } from '../utils/departments';
import { previewMatricule } from '../utils/matricule';
import api from '../services/api';
import './UserFormModal.css';

/**
 * Add / edit user form. Field visibility and requirements follow the HR
 * "User Type Profile Data" specification:
 *   - Employee: common fields + contract (CDI/CIVP), matricule, N° CNSS, job title, date of joining
 *   - Manager:  employee fields; a manager manages the department they belong to
 *   - Intern:   common fields + department (required), internship contract,
 *               academic institution, project, direct supervisor, internship start / end dates
 * Admin is not covered by the specification: it gets the employee layout with optional HR fields.
 */

const ROLES = [
  { value: 'EMPLOYEE', label: 'Employee', icon: User, hint: 'Standard staff account' },
  { value: 'MANAGER', label: 'Manager', icon: Briefcase, hint: 'Manages departments and approvals' },
  { value: 'INTERN', label: 'Intern', icon: GraduationCap, hint: 'Internship with a supervisor' },
  { value: 'ADMIN', label: 'Admin', icon: Shield, hint: 'Full platform access' },
];

const STAFF_CONTRACTS = [
  { value: 'CDI', label: 'CDI' },
  { value: 'CIVP', label: 'CIVP' },
];

const INTERN_CONTRACTS = [
  { value: 'INTERNSHIP', label: 'Internship (Stage)' },
  { value: 'INTERNSHIP_SUMMER', label: "Summer internship (Stage d'été)" },
  { value: 'INTERNSHIP_END_OF_STUDY', label: 'End of study (Stage PFE)' },
];

const isInternshipContract = (contractType) => INTERN_CONTRACTS.some((c) => c.value === contractType);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?[\d\s]{8,20}$/;
const MIN_PASSWORD_LENGTH = 8;

const generatePassword = (length = 12) => {
  const sets = ['ABCDEFGHJKLMNPQRSTUVWXYZ', 'abcdefghijkmnopqrstuvwxyz', '23456789', '!@#$%&*?'];
  const all = sets.join('');
  const rand = (max) => {
    const buf = new Uint32Array(1);
    window.crypto.getRandomValues(buf);
    return buf[0] % max;
  };
  // One character from each set, then fill and shuffle
  const chars = sets.map((set) => set[rand(set.length)]);
  while (chars.length < length) chars.push(all[rand(all.length)]);
  for (let i = chars.length - 1; i > 0; i -= 1) {
    const j = rand(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join('');
};

const suggestUsername = (firstName, lastName) =>
  [firstName, lastName]
    .map((part) => (part || '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, ''))
    .filter(Boolean)
    .join('.');

const toDateInput = (value) => (value ? String(value).slice(0, 10) : '');

const fullName = (u) => `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.username || u.email;

const buildInitialForm = (user) => {
  if (!user) {
    return {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      cin: '',
      username: '',
      password: '',
      role: 'EMPLOYEE',
      isActive: true,
      departmentIds: [],
      contractType: 'CDI',
      cnss: '',
      jobTitle: '',
      hireDate: '',
      academicInstitution: '',
      internshipProject: '',
      supervisorId: '',
      internshipStartDate: '',
      internshipEndDate: '',
    };
  }
  const role = String(user.role || 'EMPLOYEE').toUpperCase();
  // Keep the stored contract only when it is valid for the role
  const contractFitsRole = isInternshipContract(user.contractType) === (role === 'INTERN');
  return {
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    email: user.email || '',
    phoneNumber: user.phoneNumber || '',
    cin: user.cin || '',
    username: user.username || '',
    password: '',
    role,
    isActive: user.isActive !== false,
    departmentIds: getUserDepartmentIds(user).map(Number).slice(0, 1),
    contractType: (contractFitsRole && user.contractType) || (role === 'INTERN' ? 'INTERNSHIP' : 'CDI'),
    cnss: user.cnss || '',
    jobTitle: user.jobTitle || '',
    hireDate: toDateInput(user.hireDate),
    academicInstitution: user.academicInstitution || '',
    internshipProject: user.internshipProject || '',
    supervisorId: user.supervisorId || '',
    internshipStartDate: toDateInput(user.internshipStartDate),
    internshipEndDate: toDateInput(user.internshipEndDate),
  };
};

const getErrorMessage = (err) => {
  const message = err?.response?.data?.message || err?.message || 'Unknown error occurred';
  return Array.isArray(message) ? message.join(' • ') : String(message);
};

const Field = ({ label, htmlFor, required, optional, error, hint, full, children }) => (
  <div className={`uf-field${full ? ' uf-field--full' : ''}${error ? ' uf-field--invalid' : ''}`}>
    <label htmlFor={htmlFor}>
      {label}
      {required && <span className="uf-req" aria-hidden="true">*</span>}
      {optional && <span className="uf-opt">Optional</span>}
    </label>
    {children}
    {error
      ? <span className="uf-error"><AlertCircle size={12} /> {error}</span>
      : hint && <span className="uf-hint">{hint}</span>}
  </div>
);

const UserFormModal = ({ user, users = [], departments = [], canAssignAdmin = false, onSave, onClose }) => {
  const isEdit = Boolean(user);
  const [form, setForm] = useState(() => buildInitialForm(user));
  const [matriculeFormat, setMatriculeFormat] = useState(null);
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);
  // On create, the username follows the name until it is edited by hand
  const usernameEdited = useRef(isEdit);
  const supervisorEdited = useRef(isEdit && Boolean(user?.supervisorId));
  const formRef = useRef(null);
  const bodyRef = useRef(null);

  // The next sequence numbers, to show the matricule the new user will receive
  useEffect(() => {
    if (isEdit) return;
    api.get('/matricule/format').then(setMatriculeFormat).catch(() => setMatriculeFormat(null));
  }, [isEdit]);

  const { role } = form;
  const isIntern = role === 'INTERN';
  const isManager = role === 'MANAGER';
  const isAdminRole = role === 'ADMIN';
  // Employee and Manager HR fields are mandatory; Admin shows them as optional
  const hrRequired = role === 'EMPLOYEE' || isManager;
  const contractOptions = isIntern ? INTERN_CONTRACTS : STAFF_CONTRACTS;

  const roleOptions = ROLES.filter((r) => r.value !== 'ADMIN' || canAssignAdmin || isAdminRole);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const touch = (name) => setTouched((prev) => (prev[name] ? prev : { ...prev, [name]: true }));

  const changeName = (key, value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (!usernameEdited.current) {
        next.username = suggestUsername(next.firstName, next.lastName);
      }
      return next;
    });
  };

  // Switching role activates its fields and pre-selects the linked values
  const changeRole = (nextRole) => {
    setForm((prev) => {
      const next = { ...prev, role: nextRole };
      if (nextRole === 'INTERN') {
        if (!isInternshipContract(prev.contractType)) next.contractType = 'INTERNSHIP';
      } else if (isInternshipContract(prev.contractType)) {
        next.contractType = 'CDI';
      }
      return next;
    });
  };

  // A user belongs to one department
  const changeDepartment = (value) => {
    touch('departmentIds');
    const deptId = value ? Number(value) : null;
    setForm((prev) => ({ ...prev, departmentIds: deptId ? [deptId] : [] }));
  };

  // Active employees / managers, those linked to the intern's departments first
  const supervisorGroups = useMemo(() => {
    const selected = new Set(form.departmentIds.map(String));
    const managedIds = (u) => (u.managedDepartments || []).map((d) => String(d.id));
    const candidates = users
      .filter((u) => u.isActive !== false
        && ['employee', 'manager'].includes(String(u.role || '').toLowerCase())
        && u.id !== user?.id)
      .sort((a, b) => fullName(a).localeCompare(fullName(b)));
    const isLinked = (u) => [...getUserDepartmentIds(u), ...managedIds(u)].some((id) => selected.has(id));
    return {
      linked: candidates.filter(isLinked),
      others: candidates.filter((u) => !isLinked(u)),
      managing: candidates.filter((u) => String(u.role || '').toLowerCase() === 'manager'
        && managedIds(u).some((id) => selected.has(id))),
    };
  }, [users, user, form.departmentIds]);

  // Pre-select the supervisor when a single manager manages the intern's department(s)
  useEffect(() => {
    if (!isIntern || supervisorEdited.current || form.departmentIds.length === 0) return;
    const autoId = supervisorGroups.managing.length === 1 ? supervisorGroups.managing[0].id : '';
    if (autoId !== form.supervisorId) setForm((prev) => ({ ...prev, supervisorId: autoId }));
  }, [isIntern, supervisorGroups, form.departmentIds, form.supervisorId]);

  const errors = useMemo(() => {
    const e = {};
    const others = users.filter((u) => u.id !== user?.id);
    const taken = (key, value) => {
      const v = String(value || '').trim().toLowerCase();
      return Boolean(v) && others.some((u) => String(u[key] || '').trim().toLowerCase() === v);
    };

    if (!form.firstName.trim()) e.firstName = 'First name is required';
    if (!form.lastName.trim()) e.lastName = 'Last name is required';

    if (!form.email.trim()) e.email = 'Email is required';
    else if (!EMAIL_RE.test(form.email.trim())) e.email = 'Enter a valid email address';
    else if (taken('email', form.email)) e.email = 'This email is already used';

    if (!form.phoneNumber.trim()) {
      if (!isAdminRole) e.phoneNumber = 'Phone number is required';
    } else if (!PHONE_RE.test(form.phoneNumber.trim())) {
      e.phoneNumber = 'Enter a valid phone number (8 to 20 digits)';
    }

    if (!form.cin) e.cin = 'CIN is required';
    else if (!/^\d{8}$/.test(form.cin)) e.cin = 'CIN must be exactly 8 digits';
    else if (taken('cin', form.cin)) e.cin = 'This CIN is already used';

    if (!form.username.trim()) e.username = 'Username is required';
    else if (form.username.trim().length < 3) e.username = 'Username must be at least 3 characters';
    else if (taken('username', form.username)) e.username = 'This username is already used';

    if (!isEdit) {
      if (!form.password) e.password = 'Password is required';
      else if (form.password.length < MIN_PASSWORD_LENGTH) {
        e.password = `Password must be at least ${MIN_PASSWORD_LENGTH} characters`;
      }
    }

    if (!isEdit && !isIntern && form.departmentIds.length === 0) {
      e.departmentIds = 'Select a department: its code is part of the matricule';
    } else if (isManager && form.departmentIds.length === 0) {
      e.departmentIds = 'Select the department this manager manages';
    }

    if (isIntern) {
      if (form.departmentIds.length === 0) e.departmentIds = 'Select a department';
      if (!form.academicInstitution.trim()) e.academicInstitution = 'Academic institution is required';
      if (!form.internshipProject.trim()) e.internshipProject = 'Project is required';
      if (!form.supervisorId) e.supervisorId = 'Select a direct supervisor';
      if (!form.internshipStartDate) e.internshipStartDate = 'Start date is required';
      if (!form.internshipEndDate) e.internshipEndDate = 'End date is required';
      else if (form.internshipStartDate && form.internshipEndDate < form.internshipStartDate) {
        e.internshipEndDate = 'End date cannot be before the start date';
      }
    } else {
      if (hrRequired && !form.cnss.trim()) e.cnss = 'N° CNSS is required';
      else if (taken('cnss', form.cnss)) e.cnss = 'This N° CNSS is already used';

      if (hrRequired && !form.jobTitle.trim()) e.jobTitle = 'Job title is required';
      if (hrRequired && !form.hireDate) e.hireDate = 'Date of joining is required';
    }
    return e;
  }, [form, users, user, isEdit, isIntern, isManager, isAdminRole, hrRequired]);

  const errorCount = Object.keys(errors).length;
  const errorFor = (name) => ((submitted || touched[name]) ? errors[name] : undefined);

  const inputProps = (name) => ({
    id: `uf-${name}`,
    value: form[name],
    onChange: (e) => set({ [name]: e.target.value }),
    onBlur: () => touch(name),
    autoComplete: 'off',
    'aria-invalid': Boolean(errorFor(name)),
  });

  const handleGeneratePassword = () => {
    set({ password: generatePassword() });
    setShowPassword(true);
    setCopied(false);
  };

  const handleCopyPassword = async () => {
    try {
      await navigator.clipboard.writeText(form.password);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable */ }
  };

  const buildPayload = () => {
    const trimOrNull = (value) => String(value || '').trim() || null;
    // Only the fields that apply to the selected role are sent
    const payload = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      username: form.username.trim(),
      phoneNumber: trimOrNull(form.phoneNumber),
      cin: form.cin,
      role: role.toLowerCase(),
      isActive: Boolean(form.isActive),
      contractType: form.contractType,
      departmentIds: form.departmentIds.map(Number),
      departmentId: form.departmentIds.length > 0 ? Number(form.departmentIds[0]) : null,
    };

    if (isIntern) {
      Object.assign(payload, {
        academicInstitution: trimOrNull(form.academicInstitution),
        internshipProject: trimOrNull(form.internshipProject),
        supervisorId: form.supervisorId ? Number(form.supervisorId) : null,
        internshipStartDate: form.internshipStartDate || null,
        internshipEndDate: form.internshipEndDate || null,
      });
    } else {
      Object.assign(payload, {
        cnss: trimOrNull(form.cnss),
        jobTitle: trimOrNull(form.jobTitle),
        hireDate: form.hireDate || null,
        managedDepartmentIds: isManager ? form.departmentIds.map(Number) : [],
      });
    }

    if (!isEdit) payload.password = form.password;
    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (errorCount > 0) {
      requestAnimationFrame(() => {
        formRef.current
          ?.querySelector('.uf-field--invalid')
          ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      });
      return;
    }

    setSaving(true);
    setServerError('');
    try {
      await onSave(buildPayload());
    } catch (err) {
      setServerError(getErrorMessage(err));
      setSaving(false);
      if (bodyRef.current) bodyRef.current.scrollTop = 0;
    }
  };

  const activeRole = ROLES.find((r) => r.value === role) || ROLES[0];

  const matriculeDepartment = departments.find((d) => Number(d.id) === Number(form.departmentIds[0]));
  const matriculePreview = isEdit
    ? { value: user.matricule || '', hint: 'Cannot be changed' }
    : previewMatricule({
      format: matriculeFormat,
      department: matriculeDepartment,
      isIntern,
      joiningDate: isIntern ? form.internshipStartDate : form.hireDate,
    });

  return (
    <div className="modal-overlay">
      <div className="uf-modal" role="dialog" aria-modal="true" aria-labelledby="uf-title">
        <div className="uf-header">
          <div>
            <h2 id="uf-title">{isEdit ? 'Edit User' : 'Add New User'}</h2>
            <p>
              {isEdit
                ? <>Matricule <code>{user.matricule || 'N/A'}</code> · fields adapt to the selected role</>
                : 'Pick a role first: the form adapts to the required profile fields.'}
            </p>
          </div>
          <button type="button" className="uf-close" onClick={onClose} title="Close" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <form ref={formRef} onSubmit={handleSubmit} autoComplete="off" noValidate className="uf-form">
          {/* Decoy fields so the browser does not autofill the real credentials inputs */}
          <input type="text" name="username" autoComplete="username" tabIndex={-1} className="uf-decoy" aria-hidden="true" />
          <input type="password" name="password" autoComplete="current-password" tabIndex={-1} className="uf-decoy" aria-hidden="true" />

          <div className="uf-body" ref={bodyRef}>
            {serverError && (
              <div className="uf-alert" role="alert">
                <AlertCircle size={16} />
                <span>{serverError}</span>
              </div>
            )}

            <section className="uf-section">
              <h3>Role</h3>
              <div className="uf-roles" role="radiogroup" aria-label="Role">
                {roleOptions.map((r) => {
                  const Icon = r.icon;
                  const selected = r.value === role;
                  return (
                    <button
                      key={r.value}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      className={`uf-role${selected ? ' uf-role--on' : ''}`}
                      onClick={() => changeRole(r.value)}
                    >
                      <Icon size={18} />
                      <span className="uf-role-label">{r.label}</span>
                      <span className="uf-role-hint">{r.hint}</span>
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="uf-section">
              <h3>Personal information</h3>
              <div className="uf-grid">
                <Field label="First Name" htmlFor="uf-firstName" required error={errorFor('firstName')}>
                  <input
                    type="text"
                    {...inputProps('firstName')}
                    onChange={(e) => changeName('firstName', e.target.value)}
                    maxLength={100}
                    autoFocus={!isEdit}
                  />
                </Field>
                <Field label="Last Name" htmlFor="uf-lastName" required error={errorFor('lastName')}>
                  <input
                    type="text"
                    {...inputProps('lastName')}
                    onChange={(e) => changeName('lastName', e.target.value)}
                    maxLength={100}
                  />
                </Field>
                <Field label="Email" htmlFor="uf-email" required error={errorFor('email')}>
                  <input type="email" {...inputProps('email')} maxLength={255} placeholder="name@company.com" />
                </Field>
                <Field
                  label="Phone Number"
                  htmlFor="uf-phoneNumber"
                  required={!isAdminRole}
                  optional={isAdminRole}
                  error={errorFor('phoneNumber')}
                >
                  <input
                    type="tel"
                    {...inputProps('phoneNumber')}
                    onChange={(e) => set({ phoneNumber: e.target.value.replace(/[^\d+\s]/g, '').slice(0, 20) })}
                    placeholder="+216 12 345 678"
                  />
                </Field>
                <Field label="CIN" htmlFor="uf-cin" required error={errorFor('cin')} hint="8 digits">
                  <input
                    type="text"
                    inputMode="numeric"
                    {...inputProps('cin')}
                    onChange={(e) => set({ cin: e.target.value.replace(/\D/g, '').slice(0, 8) })}
                    maxLength={8}
                    placeholder="12345678"
                  />
                </Field>
              </div>
            </section>

            <section className="uf-section">
              <h3>Account</h3>
              <div className="uf-grid">
                <Field
                  label="Username"
                  htmlFor="uf-username"
                  required
                  error={errorFor('username')}
                  hint={!isEdit && !usernameEdited.current ? 'Suggested from the name' : undefined}
                >
                  <input
                    type="text"
                    {...inputProps('username')}
                    onChange={(e) => {
                      usernameEdited.current = true;
                      set({ username: e.target.value });
                    }}
                    maxLength={50}
                  />
                </Field>
                <Field label="Status" required>
                  <div className="uf-segmented" role="radiogroup" aria-label="Status">
                    <button
                      type="button"
                      role="radio"
                      aria-checked={form.isActive}
                      className={`uf-seg uf-seg--active${form.isActive ? ' uf-seg--on' : ''}`}
                      onClick={() => set({ isActive: true })}
                    >
                      Active
                    </button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={!form.isActive}
                      className={`uf-seg uf-seg--inactive${!form.isActive ? ' uf-seg--on' : ''}`}
                      onClick={() => set({ isActive: false })}
                    >
                      Inactive
                    </button>
                  </div>
                </Field>
                {!isEdit && (
                  <Field
                    label="Password"
                    htmlFor="uf-password"
                    required
                    full
                    error={errorFor('password')}
                    hint={`At least ${MIN_PASSWORD_LENGTH} characters. Share it with the user after creation.`}
                  >
                    <div className="uf-password">
                      <div className="uf-password-input">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          {...inputProps('password')}
                          autoComplete="new-password"
                          maxLength={128}
                        />
                        <button
                          type="button"
                          className="uf-icon-btn"
                          onClick={() => setShowPassword((v) => !v)}
                          title={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                      <button type="button" className="uf-tool-btn" onClick={handleGeneratePassword}>
                        <RefreshCw size={14} /> Auto-generate
                      </button>
                      <button
                        type="button"
                        className="uf-tool-btn"
                        onClick={handleCopyPassword}
                        disabled={!form.password}
                      >
                        {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                  </Field>
                )}
              </div>
            </section>

            <section className="uf-section">
              <h3>{isIntern ? 'Internship' : 'Employment'} <span className="uf-section-tag">{activeRole.label}</span></h3>
              <div className="uf-grid">
                <Field
                  label="Department"
                  htmlFor="uf-department"
                  required={isIntern || isManager || !isEdit}
                  optional={!isIntern && !isManager && isEdit}
                  error={errorFor('departmentIds')}
                  hint={isManager
                    ? 'The manager approves the requests of this department'
                    : (isEdit ? undefined : 'Its code is part of the matricule')}
                >
                  <select
                    id="uf-department"
                    value={form.departmentIds[0] || ''}
                    onChange={(e) => changeDepartment(e.target.value)}
                    onBlur={() => touch('departmentIds')}
                  >
                    <option value="">{departments.length === 0 ? 'No departments available' : 'Select a department'}</option>
                    {departments.map((dept) => (
                      <option key={dept.id} value={dept.id}>{dept.name}</option>
                    ))}
                  </select>
                </Field>

                <Field
                  label="Contract / User Type"
                  htmlFor="uf-contractType"
                  required
                  hint={isIntern ? 'Internship contract selected for the Intern role' : undefined}
                >
                  <select {...inputProps('contractType')}>
                    {contractOptions.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </Field>

                <Field label="Matricule" htmlFor="uf-matricule" hint={matriculePreview.hint}>
                  <input
                    id="uf-matricule"
                    type="text"
                    value={matriculePreview.value}
                    placeholder="Assigned automatically"
                    readOnly
                    disabled
                  />
                </Field>

                {!isIntern && (
                  <>
                    <Field
                      label="N° CNSS"
                      htmlFor="uf-cnss"
                      required={hrRequired}
                      optional={!hrRequired}
                      error={errorFor('cnss')}
                    >
                      <input
                        type="text"
                        inputMode="numeric"
                        {...inputProps('cnss')}
                        onChange={(e) => set({ cnss: e.target.value.replace(/[^\d-]/g, '').slice(0, 50) })}
                        placeholder="CNSS number"
                      />
                    </Field>
                    <Field
                      label="Job Title / Position"
                      htmlFor="uf-jobTitle"
                      required={hrRequired}
                      optional={!hrRequired}
                      error={errorFor('jobTitle')}
                    >
                      <input type="text" {...inputProps('jobTitle')} maxLength={150} placeholder="e.g. Software Engineer" />
                    </Field>
                    <Field
                      label="Date of Joining"
                      htmlFor="uf-hireDate"
                      required={hrRequired}
                      optional={!hrRequired}
                      error={errorFor('hireDate')}
                    >
                      <input type="date" {...inputProps('hireDate')} />
                    </Field>
                  </>
                )}

                {isIntern && (
                  <>
                    <Field
                      label="Academic Institution"
                      htmlFor="uf-academicInstitution"
                      required
                      error={errorFor('academicInstitution')}
                    >
                      <input
                        type="text"
                        {...inputProps('academicInstitution')}
                        maxLength={200}
                        placeholder="Name of university / school"
                      />
                    </Field>
                    <Field label="Project" htmlFor="uf-internshipProject" required error={errorFor('internshipProject')}>
                      <input type="text" {...inputProps('internshipProject')} maxLength={200} />
                    </Field>
                    <Field
                      label="Direct Supervisor"
                      htmlFor="uf-supervisorId"
                      required
                      error={errorFor('supervisorId')}
                      hint="Active employees and managers"
                    >
                      <select
                        {...inputProps('supervisorId')}
                        onChange={(e) => {
                          supervisorEdited.current = true;
                          set({ supervisorId: e.target.value ? Number(e.target.value) : '' });
                        }}
                      >
                        <option value="">Select a supervisor…</option>
                        {supervisorGroups.linked.length > 0 && (
                          <optgroup label="Same department">
                            {supervisorGroups.linked.map((u) => (
                              <option key={u.id} value={u.id}>
                                {fullName(u)} ({String(u.role).toLowerCase()})
                              </option>
                            ))}
                          </optgroup>
                        )}
                        {supervisorGroups.others.length > 0 && (
                          <optgroup label={supervisorGroups.linked.length > 0 ? 'Other departments' : 'All'}>
                            {supervisorGroups.others.map((u) => (
                              <option key={u.id} value={u.id}>
                                {fullName(u)} ({String(u.role).toLowerCase()})
                              </option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                    </Field>
                    <div className="uf-field uf-field--spacer" aria-hidden="true" />
                    <Field
                      label="Internship Start Date"
                      htmlFor="uf-internshipStartDate"
                      required
                      error={errorFor('internshipStartDate')}
                    >
                      <input type="date" {...inputProps('internshipStartDate')} max={form.internshipEndDate || undefined} />
                    </Field>
                    <Field
                      label="Internship End Date"
                      htmlFor="uf-internshipEndDate"
                      required
                      error={errorFor('internshipEndDate')}
                    >
                      <input type="date" {...inputProps('internshipEndDate')} min={form.internshipStartDate || undefined} />
                    </Field>
                  </>
                )}
              </div>
            </section>
          </div>

          <div className="uf-footer">
            <span className="uf-footer-note">
              {submitted && errorCount > 0
                ? <span className="uf-error"><AlertCircle size={12} /> {errorCount} field{errorCount === 1 ? '' : 's'} need attention</span>
                : <><span className="uf-req">*</span> Required fields</>}
            </span>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : (isEdit ? 'Update' : `Create ${activeRole.label}`)}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserFormModal;
